import { zodResolver } from "@hookform/resolvers/zod"
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  Form,
  RadioItem,
  Separator,
  Text,
  useRadioGroup,
  useToast,
} from "@prismicio/editor-ui"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { useId } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { HookFormInput } from "../../../app/components/hook-form/HookFormInput"
import { HookFormRadioGroup } from "../../../app/components/hook-form/HookFormRadioGroup"
import { HookFormTextArea } from "../../../app/components/hook-form/HookFormTextArea"
import { useAiTranslationFeedbackFormVariantFlag } from "../../../app/integrations/AmplitudeExperiments"
import { invalidateRepositoryV2 } from "../../../app/services/repository"
import {
  AiTranslationFeedbackBody,
  getAiTranslationFeedbackOptions,
  invalidateAiTranslationFeedback,
  postAiTranslationFeedback,
} from "../../../app/services/translateDocument"

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AiTranslationFeedbackDialog(props: FeedbackDialogProps) {
  const { open, onOpenChange } = props

  const { data: aiTranslationFeedback } = useSuspenseQuery(
    getAiTranslationFeedbackOptions(),
  )
  const { repositorySubmitted } = aiTranslationFeedback

  const { showToast } = useToast()

  const { mutate: submitFeedback, isPending } = useMutation({
    mutationFn: postAiTranslationFeedback,
    onError: () => {
      showToast({
        title: "Failed to submit your feedback",
        icon: "block",
      })
    },
    onSuccess: async () => {
      const eligibleForFreeTranslations = !repositorySubmitted
      await Promise.all([
        // Invalidate repository to refresh free translations left quota
        eligibleForFreeTranslations && invalidateRepositoryV2(),
        invalidateAiTranslationFeedback(),
      ])
      showToast({
        title: eligibleForFreeTranslations
          ? "Feedback submitted successfully. You've earned 5 more free translations!"
          : "Feedback submitted successfully.",
      })
      onOpenChange(false)
    },
  })

  const formVariant = useAiTranslationFeedbackFormVariantFlag()

  const form = useForm<AiTranslationFeedbackBody>({
    resolver: zodResolver(AiTranslationFeedbackBody),
    defaultValues: {
      formVariant: formVariant,
    },
  })

  // id used for linking native form and submit button
  const formId = useId()

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size={{ height: "auto", width: 656 }}
      noDescription
    >
      <DialogHeader icon="help" title="Help us improve our AI Translations" />
      <DialogContent padding={16} gap={0}>
        {!repositorySubmitted && (
          <>
            <Text>
              Help us improve our service to get 5 additional free translations.
            </Text>
            <Separator style="dashed" sx={{ marginBlock: 16 }} />
          </>
        )}
        <FormProvider {...form}>
          <Form
            id={formId}
            onSubmit={form.handleSubmit((data) =>
              submitFeedback({ body: data }),
            )}
          >
            <div>
              <HookFormRadioGroup
                control={form.control}
                name="translationQualitySatisfaction"
                label="How satisfied were you with the quality of the translations made with Translate with AI?"
              >
                <ButtonGroup density="compact" size="large">
                  <EmojiRadioItem emoji="😠" value="1" />
                  <EmojiRadioItem emoji="🙁" value="2" />
                  <EmojiRadioItem emoji="😐" value="3" />
                  <EmojiRadioItem emoji="🙂" value="4" />
                  <EmojiRadioItem emoji="😍" value="5" />
                </ButtonGroup>
              </HookFormRadioGroup>
            </div>

            <HookFormTextArea
              control={form.control}
              name="whatWorkedWell"
              label="What worked well in your latest translation? (Name two things)"
            />

            <HookFormTextArea
              control={form.control}
              name="whatCouldBeImproved"
              label="What could be improved in your latest translation? (Name two things)"
            />

            <HookFormInput
              control={form.control}
              name="pagesTranslatedPerMonth"
              label="How many pages are you currently translating per month across all locales?"
              type="number"
            />

            <div>
              <Text variant="emphasized" sx={{ marginBottom: 4 }}>
                At what price ($) per{" "}
                {formVariant === "perWord" ? "word" : "page"} would you consider
                the AI translation:
              </Text>

              <Box
                flexDirection="column"
                gap={16}
                padding={16}
                border
                borderRadius={8}
              >
                <Box gap={8} justifyContent="space-between" alignItems="center">
                  <Text>
                    <Text variant="bold">Too Cheap:</Text>
                    <br />
                    At what price would it be so low that you start to question
                    the quality?
                  </Text>
                  <HookFormInput
                    control={form.control}
                    name={
                      formVariant === "perWord"
                        ? "priceTooCheapPerWord"
                        : "priceTooCheapPerPage"
                    }
                    type="number"
                    prefix="$"
                    sx={{ width: 100 }}
                  />
                </Box>

                <Box gap={8} justifyContent="space-between" alignItems="center">
                  <Text>
                    <Text variant="bold">Good Value:</Text>
                    <br />
                    At what price would it be a good buy for the money?
                  </Text>
                  <HookFormInput
                    control={form.control}
                    name={
                      formVariant === "perWord"
                        ? "priceGoodValuePerWord"
                        : "priceGoodValuePerPage"
                    }
                    type="number"
                    prefix="$"
                    sx={{ width: 100 }}
                  />
                </Box>

                <Box gap={8} justifyContent="space-between" alignItems="center">
                  <Text>
                    <Text variant="bold">Getting Expensive:</Text>
                    <br />
                    At what price would it start to feel pricey, but still worth
                    considering?
                  </Text>
                  <HookFormInput
                    control={form.control}
                    name={
                      formVariant === "perWord"
                        ? "priceGettingExpensivePerWord"
                        : "priceGettingExpensivePerPage"
                    }
                    type="number"
                    prefix="$"
                    sx={{ width: 100 }}
                  />
                </Box>

                <Box gap={8} justifyContent="space-between" alignItems="center">
                  <Text>
                    <Text variant="bold">Too Expensive:</Text>
                    <br />
                    At what price would it be too expensive to consider buying?
                  </Text>
                  <HookFormInput
                    control={form.control}
                    name={
                      formVariant === "perWord"
                        ? "priceTooExpensivePerWord"
                        : "priceTooExpensivePerPage"
                    }
                    type="number"
                    prefix="$"
                    sx={{ width: 100 }}
                  />
                </Box>
              </Box>
            </div>
          </Form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <DialogCancelButton disabled={isPending} />
        <DialogActionButton type="submit" form={formId} loading={isPending}>
          {repositorySubmitted ? "Submit feedback" : "Submit & get 5 more"}
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

interface EmojiRadioButtonProps {
  emoji: string
  value: string
  onClick?: () => void
}

function EmojiRadioItem(props: EmojiRadioButtonProps) {
  const { emoji, value, onClick } = props
  const { selectedValue } = useRadioGroup()

  const isSelected = selectedValue === value

  return (
    <RadioItem value={value} asChild>
      <Button color={isSelected ? "purple" : "grey"} onClick={onClick}>
        <span style={{ fontSize: "22px", lineHeight: "24px" }}>{emoji}</span>
      </Button>
    </RadioItem>
  )
}
